import { useSyncExternalStore } from 'react';

/**
 * Riders this player chose to hide, on this device only.
 *
 * A hidden rider still rides (hiding changes nothing about rounds or results);
 * everywhere a name is drawn they appear as their generated nickname in their
 * default colour, never their chosen name or colour. Stored as a JSON array of
 * public rider ids under 'slopeHiddenRiders'.
 */
export const HIDDEN_RIDERS_KEY = 'slopeHiddenRiders';
const MAX_HIDDEN_RIDERS = 500;
const RIDER_ID = /^[A-Za-z0-9_-]{1,64}$/;

const EMPTY: readonly string[] = Object.freeze([]);
let cache: readonly string[] | null = null;
let lookup = new Set<string>();
const listeners = new Set<() => void>();

function readStored(): readonly string[] {
  try {
    const raw = window.localStorage.getItem(HIDDEN_RIDERS_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return EMPTY;
    const ids = parsed.filter((id): id is string => typeof id === 'string' && RIDER_ID.test(id));
    return Object.freeze(Array.from(new Set(ids)).slice(-MAX_HIDDEN_RIDERS));
  } catch {
    // Private browsing, blocked storage or a damaged value: nothing hidden.
    return EMPTY;
  }
}

function load(): readonly string[] {
  if (cache === null) {
    cache = readStored();
    lookup = new Set(cache);
  }
  return cache;
}

function notify() {
  listeners.forEach(listener => listener());
}

function store(next: string[]) {
  cache = Object.freeze(next.slice(-MAX_HIDDEN_RIDERS));
  lookup = new Set(cache);
  try {
    window.localStorage.setItem(HIDDEN_RIDERS_KEY, JSON.stringify(cache));
  } catch {
    // Storage unavailable: the rider stays hidden until the app is closed.
  }
  notify();
}

export const getHiddenRiders = (): readonly string[] => load();

/** Cheap enough for the canvas to ask every frame. */
export function isRiderHidden(playerId?: string | null): boolean {
  if (!playerId) return false;
  load();
  return lookup.has(playerId);
}

export function hideRider(playerId: string) {
  if (!RIDER_ID.test(playerId) || isRiderHidden(playerId)) return;
  store([...load(), playerId]);
}

export function unhideRider(playerId: string) {
  if (!isRiderHidden(playerId)) return;
  store(load().filter(id => id !== playerId));
}

/** Re-reads storage, e.g. after another tab changed it. */
export function reloadHiddenRiders() {
  cache = null;
  load();
  notify();
}

export function subscribeHiddenRiders(listener: () => void) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/** The hidden rider ids; re-renders the caller when the list changes. */
export function useHiddenRiders(): readonly string[] {
  return useSyncExternalStore(subscribeHiddenRiders, getHiddenRiders, getHiddenRiders);
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', event => {
    if (event.key === HIDDEN_RIDERS_KEY || event.key === null) reloadHiddenRiders();
  });
}
